import {
  ChatClient,
  ChatMessageReceivedEvent,
  ChatThreadCreatedEvent,
  ChatThreadDeletedEvent,
  ChatThreadPropertiesUpdatedEvent,
  TypingIndicatorReceivedEvent,
} from '@azure/communication-chat';
import { AzureCommunicationTokenCredential } from '@azure/communication-common';

type EventHandler = (data: any) => void;

/**
 * Gestionnaire de la connexion temps réel Azure Communication Services.
 * Responsabilité unique : cycle de vie du ChatClient + dispatch d'événements internes.
 */
export class AzureChatManager {
  private chatClient: ChatClient | null = null;
  private isRealtimeStarted = false;
  private eventHandlers: Map<string, EventHandler[]> = new Map();

  async initialize(acsEndpoint: string, token: string): Promise<void> {
    const tokenCredential = new AzureCommunicationTokenCredential(token);
    this.chatClient = new ChatClient(acsEndpoint, tokenCredential);
    await this.startRealtimeNotifications();
    this.setupEventListeners();
  }

  private async startRealtimeNotifications(): Promise<void> {
    if (!this.chatClient || this.isRealtimeStarted) return;
    await this.chatClient.startRealtimeNotifications();
    this.isRealtimeStarted = true;
  }

  private setupEventListeners(): void {
    if (!this.chatClient) return;

    this.chatClient.on('chatMessageReceived', (event: ChatMessageReceivedEvent) => {
      this.emitEvent('messageReceived', event);
    });

    this.chatClient.on('chatThreadCreated', (event: ChatThreadCreatedEvent) => {
      this.emitEvent('threadCreated', event);
    });

    this.chatClient.on('chatThreadPropertiesUpdated', (event: ChatThreadPropertiesUpdatedEvent) => {
      this.emitEvent('threadUpdated', event);
    });

    this.chatClient.on('chatThreadDeleted', (event: ChatThreadDeletedEvent) => {
      this.emitEvent('threadDeleted', event);
    });

    this.chatClient.on('typingIndicatorReceived', (event: TypingIndicatorReceivedEvent) => {
      this.emitEvent('typingIndicator', event);
    });
  }

  addEventListener(eventType: string, handler: EventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler);
  }

  removeEventListener(eventType: string, handler: EventHandler): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  private emitEvent(eventType: string, data: any): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    }
  }

  async sendTypingNotification(threadId: string): Promise<void> {
    if (!this.chatClient) return;
    const threadClient = this.chatClient.getChatThreadClient(threadId);
    await threadClient.sendTypingNotification();
  }

  async markMessagesAsRead(threadId: string, messageId: string): Promise<void> {
    if (!this.chatClient) return;
    const threadClient = this.chatClient.getChatThreadClient(threadId);
    await threadClient.sendReadReceipt({ chatMessageId: messageId });
  }

  async cleanup(): Promise<void> {
    if (this.chatClient && this.isRealtimeStarted) {
      await this.chatClient.stopRealtimeNotifications();
      this.isRealtimeStarted = false;
      this.eventHandlers.clear();
    }
  }

  getChatClient(): ChatClient | null {
    return this.chatClient;
  }
}
